<template>
    <div
        ref="anchor"
        :class="['dropdown schedule-row-picker', { focused: isListOpen }]"
    >
        <input
            :id="id"
            ref="input"
            v-model="text"
            type="text"
            class="input-no-border"
            :placeholder="modelValue ? modelValue.name : placeholder"
            @click="onInputClick"
            @keydown.enter.prevent="onInputEnter"
            @keydown.down.prevent="onArrowDown"
            @keydown.up.prevent="onArrowUp"
        />
        <teleport to="body">
            <div
                v-if="isListOpen"
                ref="list"
                v-focus-out="onCloseList"
                :class="[
                    'list-wrapper list-wrapper--schedule-row',
                    { 'list-wrapper--flipped': isListFlipped },
                ]"
                :style="listStyle"
            >
                <ul>
                    <template v-if="visibleTrips.length">
                        <li class="group-label">Trips</li>
                        <li
                            v-for="option in visibleTrips"
                            :key="`trip-${option.refId}`"
                            :class="{
                                highlighted: highlightedKey === option.key,
                            }"
                            @click="onSelectOption(option)"
                            @mouseover="highlightedKey = option.key"
                        >
                            {{ option.name }}
                        </li>
                    </template>
                    <template v-if="visibleIdeas.length">
                        <li class="group-label">Ideas</li>
                        <li
                            v-for="option in visibleIdeas"
                            :key="`idea-${option.refId}`"
                            :class="{
                                highlighted: highlightedKey === option.key,
                            }"
                            @click="onSelectOption(option)"
                            @mouseover="highlightedKey = option.key"
                        >
                            {{ option.name }}
                        </li>
                    </template>
                    <li
                        v-if="!visibleTrips.length && !visibleIdeas.length"
                        class="no-matches"
                    >
                        No matches found.
                    </li>
                    <li v-if="hiddenCount > 0" class="hidden-count-hint">
                        +{{ hiddenCount }} more — keep typing to narrow down
                    </li>
                    <li
                        v-if="text.trim().length"
                        class="new-entry"
                        @click="onAddCustom()"
                    >
                        <span>Add "{{ uppercaseText }}" as a custom row</span>
                    </li>
                </ul>
            </div>
        </teleport>
    </div>
</template>

<script>
import BaseDropdownPosition from "js/components/base/BaseDropdownPosition.js";
import { useTripStore } from "js/stores/tripStore";
import { useSavedIdeaStore } from "js/stores/savedIdeaStore";
import { shortenScheduleName } from "js/lib/scheduleName.js";

function durationDays(startDate, endDate) {
    if (!startDate || !endDate) {
        return null;
    }
    const start = new Date(startDate);
    const end = new Date(endDate);
    return Math.round((end - start) / 86400000) + 1;
}

function splitLimit(aLength, bLength, total) {
    const half = Math.ceil(total / 2);
    let aCount = Math.min(aLength, half);
    let bCount = Math.min(bLength, total - aCount);
    aCount = Math.min(aLength, total - bCount);
    return [aCount, bCount];
}

export default {
    name: "AddScheduleRow",
    extends: BaseDropdownPosition,
    props: {
        year: {
            type: Number,
            required: true,
        },
        maxOptions: {
            type: Number,
            default: 10,
        },
        id: {
            type: String,
            default: "add-schedule-row",
        },
        placeholder: {
            type: String,
            default: "Add a trip, idea, or your own…",
        },
        modelValue: {
            type: Object,
            default: null,
        },
        excluded: {
            type: Array,
            default: () => [],
        },
    },
    emits: ["add"],
    data() {
        return {
            isListOpen: false,
            highlightedKey: "",
            text: "",
            options: [],
        };
    },
    computed: {
        excludedKeys() {
            return new Set(this.excluded.map((e) => `${e.type}-${e.refId}`));
        },
        filteredOptions() {
            const q = this.text.trim().toLowerCase();
            const notExcluded = (o) =>
                !this.excludedKeys.has(`${o.type}-${o.refId}`);
            if (!q) {
                return this.options.filter(notExcluded);
            }
            return this.options
                .filter((o) => o.searchText.includes(q))
                .filter(notExcluded);
        },
        filteredTrips() {
            return this.filteredOptions.filter((o) => o.type === "trip");
        },
        filteredIdeas() {
            return this.filteredOptions.filter((o) => o.type === "idea");
        },
        visibleCounts() {
            return splitLimit(
                this.filteredTrips.length,
                this.filteredIdeas.length,
                this.maxOptions
            );
        },
        visibleTrips() {
            return this.filteredTrips.slice(0, this.visibleCounts[0]);
        },
        visibleIdeas() {
            return this.filteredIdeas.slice(0, this.visibleCounts[1]);
        },
        visibleFlatOptions() {
            return [...this.visibleTrips, ...this.visibleIdeas];
        },
        hiddenCount() {
            return (
                this.filteredTrips.length -
                this.visibleTrips.length +
                (this.filteredIdeas.length - this.visibleIdeas.length)
            );
        },
        uppercaseText() {
            return (
                this.text.trim().charAt(0).toUpperCase() +
                this.text.trim().slice(1)
            );
        },
    },
    watch: {
        year() {
            this.loadTrips();
        },
    },
    async mounted() {
        await Promise.all([this.loadTrips(), this.loadIdeas()]);
    },
    methods: {
        async loadTrips() {
            const tripStore = useTripStore();
            const trips = await tripStore.fetchTrips({
                "with-locations": 1,
                year: this.year,
            });

            const tripOptions = trips.map((trip) => {
                const countries = (trip.locations || [])
                    .map((l) => l.geo && l.geo.country)
                    .filter(Boolean);

                return {
                    key: `trip-${trip.id}`,
                    type: "trip",
                    refId: trip.id,
                    name: trip.name,
                    countries,
                    searchText: [trip.name, ...countries]
                        .join(" ")
                        .toLowerCase(),
                    totalDays: durationDays(trip.start_date, trip.end_date),
                };
            });

            this.options = [
                ...this.options.filter((o) => o.type !== "trip"),
                ...tripOptions,
            ];
        },
        async loadIdeas() {
            const savedIdeaStore = useSavedIdeaStore();
            await savedIdeaStore.load();

            const ideaOptions = savedIdeaStore.list.map((idea) => {
                const countries = idea.country_names ?? idea.countries ?? [];

                return {
                    key: `idea-${idea.id}`,
                    type: "idea",
                    refId: idea.id,
                    name: idea.title,
                    countries,
                    searchText: [idea.title, ...countries]
                        .join(" ")
                        .toLowerCase(),
                    totalDays: idea.ideal_duration ?? null,
                };
            });

            this.options = [
                ...this.options.filter((o) => o.type !== "idea"),
                ...ideaOptions,
            ];
        },
        onInputClick() {
            this.isListOpen = true;
            this.highlightedKey = "";
        },
        onInputEnter() {
            const highlighted = this.visibleFlatOptions.find(
                (o) => o.key === this.highlightedKey
            );
            if (highlighted) {
                this.onSelectOption(highlighted);
            } else if (this.visibleFlatOptions.length) {
                this.onSelectOption(this.visibleFlatOptions[0]);
            } else if (this.text.trim().length) {
                this.onAddCustom();
            }
        },
        onArrowDown() {
            if (!this.isListOpen) {
                this.isListOpen = true;
            }
            const list = this.visibleFlatOptions;
            if (!list.length) {
                return;
            }
            const currentIndex = list.findIndex(
                (o) => o.key === this.highlightedKey
            );
            const nextIndex =
                currentIndex === -1
                    ? 0
                    : Math.min(currentIndex + 1, list.length - 1);
            this.highlightedKey = list[nextIndex].key;
            this.scrollHighlightedIntoView();
        },
        onArrowUp() {
            const list = this.visibleFlatOptions;
            if (!list.length) {
                return;
            }
            const currentIndex = list.findIndex(
                (o) => o.key === this.highlightedKey
            );
            const prevIndex = currentIndex <= 0 ? 0 : currentIndex - 1;
            this.highlightedKey = list[prevIndex].key;
            this.scrollHighlightedIntoView();
        },
        scrollHighlightedIntoView() {
            this.$nextTick(() => {
                this.$refs.list
                    ?.querySelector("li.highlighted")
                    ?.scrollIntoView({ block: "nearest" });
            });
        },
        onSelectOption(option) {
            this.$emit("add", {
                type: option.type,
                itemable_id: option.refId,
                name: shortenScheduleName(option.name, option.countries),
                planned_days: option.totalDays,
            });
            this.reset();
        },
        onAddCustom() {
            const name = this.uppercaseText;
            if (!name.length) {
                return;
            }
            this.$emit("add", {
                type: "custom",
                itemable_id: null,
                name,
                planned_days: null,
            });
            this.reset();
        },
        onCloseList() {
            this.isListOpen = false;
            this.highlightedKey = "";
            if (this.$refs.input) {
                this.$refs.input.blur();
            }
        },
        reset() {
            this.text = "";
            this.onCloseList();
        },
    },
};
</script>
